import { FC, useContext, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { Button, Flex, Stack, Text } from '@chakra-ui/react';

import { useUpdateApplicationDoc } from '~/core/shared/api/middle-office/application';
import { Dialog, FormField, Textarea } from '~/core/shared/components';
import { DocStatusEnum } from '~/core/shared/constants';
import { useFormResolver } from '~/core/shared/hooks';
import { Checkbox } from '~/core/shared/lib/chakraUi/components/Checkbox';
import { Doc } from '~/core/shared/types';
import { handleSuccess } from '~/core/shared/utils/admin';
import { DocsTabContext } from '../application/tabs';

type DocsRejectedReasonForm = {
  rejectionReason: string;
};

type DocRejectedModalProps = {
  doc: Doc;
};

const validationSchema = yup.object({
  rejectionReason: yup.string().trim().required(),
});

export const DocRejectedModal: FC<DocRejectedModalProps> = ({ doc }) => {
  const { docRejectedModalApi, uuid } = useContext(DocsTabContext);
  const { updateDocument, isPending } = useUpdateApplicationDoc('doc', uuid);
  const [sendRejectionEmail, setSendRejectionEmail] = useState<boolean>(false);

  // TODO: @haxxxton refactor to use form blocks
  const resolver = useFormResolver<DocsRejectedReasonForm>(validationSchema);
  const form = useForm<DocsRejectedReasonForm>({
    resolver,
  });

  const onSubmit = async (
    formValues: DocsRejectedReasonForm,
  ): Promise<void> => {
    await updateDocument({
      documentId: doc.id,
      data: {
        rejectionReason: formValues.rejectionReason,
        sendRejectionEmail,
        status: DocStatusEnum.Rejected,
      },
    });
    form.reset();
    setSendRejectionEmail(false);
    handleSuccess('Success');
    docRejectedModalApi.onClose();
  };

  return (
    <Dialog
      header="Reject Document"
      onOpenChange={docRejectedModalApi.onOpenChange}
      open={docRejectedModalApi.open}
      size="lg"
      subHeader={`${doc.name || doc.type} will be rejected`}
    >
      <FormProvider {...form}>
        <Stack as="form" gap={6} onSubmit={form.handleSubmit(onSubmit)} p={6}>
          <Text fontWeight="semibold">{doc.type}</Text>

          <FormField
            component={Textarea}
            label="Rejection Reason"
            name="rejectionReason"
            required
          />

          <Flex>
            <Checkbox
              flexShrink={0}
              mr={4}
              onCheckedChange={(e) => setSendRejectionEmail(!!e.checked)}
              size="lg"
            />

            <Text>Send document rejection email notification to Broker</Text>
          </Flex>

          <Stack direction="row" justifyContent="end">
            <Button
              colorPalette="gray"
              fontSize="md"
              fontWeight="normal"
              onClick={docRejectedModalApi.onClose}
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              colorPalette="primary"
              fontSize="md"
              fontWeight="bold"
              loading={isPending}
              type="submit"
              variant="solid"
            >
              Save
            </Button>
          </Stack>
        </Stack>
      </FormProvider>
    </Dialog>
  );
};
